import gql from "graphql-tag";

import {setAnalyticsCookies} from "@khan/auth-shared";
import {type GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {
    loginWithCleverMutationType,
    transferCleverAuthMutationType,
} from "./__graphql-types__/index.ts";

export const CLEVER_LOGIN_MUTATION: GqlOperation<
    loginWithCleverMutationType["response"],
    loginWithCleverMutationType["variables"]
> = gqlOp(gql`
    mutation loginWithCleverMutation(
        $code: String!
        $reauth: Boolean!
        $fromDevApp: Boolean!
        $isMap: Boolean!
        $isLP: Boolean!
        $isCanvas: Boolean
        $referrerUrl: String!
    ) {
        loginWithClever(
            code: $code
            reauth: $reauth
            fromDevApp: $fromDevApp
            isMap: $isMap
            isLP: $isLP
            isCanvas: $isCanvas
            referrerUrl: $referrerUrl
        ) {
            error {
                code
                cleverUserInfo {
                    cleverId
                    isTeacher
                    cleverEmail
                }
                transferToken
                udiUuid
                kaLocale
            }
            user {
                id
                kaid
                userDistrictInfos {
                    id
                    district {
                        id
                        kaLocale
                    }
                }
            }
            isLibraryUser
            isFirstLogin
            transferToken
        }
    }
`);

export const CLEVER_TRANSFER_AUTH_MUTATION: GqlOperation<
    transferCleverAuthMutationType["response"],
    transferCleverAuthMutationType["variables"]
> = gqlOp(gql`
    mutation transferCleverAuthMutation(
        $transferToken: String!
        $reauth: Boolean!
    ) {
        transferCleverAuth(transferToken: $transferToken, reauth: $reauth) {
            error {
                code
                cleverUserInfo {
                    cleverId
                    isTeacher
                    cleverEmail
                }
                transferToken
                udiUuid
                kaLocale
            }
            user {
                id
                kaid
                userDistrictInfos {
                    id
                    district {
                        id
                        kaLocale
                    }
                }
            }
            isLibraryUser
            isFirstLogin
            transferToken
        }
    }
`);

type MutationResults =
    | loginWithCleverMutationType["response"]["loginWithClever"]
    | transferCleverAuthMutationType["response"]["transferCleverAuth"];

// TODO(marksandstrom) This type has really weak guarantees. We can do
// better! Anyway, the calling code handles all these cases.
interface CleverUserInfo {
    cleverId: string | null | undefined;
    isTeacher: boolean | null | undefined;
    cleverEmail?: string | null | undefined;
}

interface CleverResultSuccess {
    resultType: "success";
    firstUserDistrictKaLocale: string | null;
    isLibraryUser: boolean | null | undefined;
    transferToken: string | null | undefined;
}

interface CleverResultError {
    resultType: "notLinked";
    cleverUserInfo: CleverUserInfo | null | undefined;
    transferToken: string;
    udiUuid: string | null | undefined;
    kaLocale: string | null | undefined;
}

export type CleverResult = CleverResultSuccess | CleverResultError;

const handleCleverMutationError = (error: Error) => {
    if (error instanceof KAError) {
        throw error;
    }

    // Throw a generic error.
    throw new KAError("Failed to login ", Errors.Internal);
};

const handleCleverMutationResults = (
    resultContents: MutationResults,
): CleverResult => {
    if (resultContents.error) {
        if (resultContents.error.code === "ACCOUNT_NOT_LINKED") {
            // The user hasn't linked their account, but we still need their
            // Clever information so we can show the Clever onboarding UI
            return {
                resultType: "notLinked",
                cleverUserInfo: resultContents.error.cleverUserInfo,
                transferToken: resultContents.error.transferToken,
                udiUuid: resultContents.error.udiUuid,
                kaLocale: resultContents.error.kaLocale,
            };
        }
        throw new KAError(resultContents.error.code, Errors.Internal);
    }

    const isFirstLogin = resultContents.isFirstLogin ?? false;

    // Set analytics cookies.
    setAnalyticsCookies(isFirstLogin, resultContents.user?.kaid || "");

    return {
        resultType: "success",
        firstUserDistrictKaLocale:
            resultContents?.user?.userDistrictInfos?.[0]?.district?.kaLocale ??
            null,
        transferToken: resultContents?.transferToken,
        isLibraryUser: resultContents?.isLibraryUser,
    };
};

/**
 * Logs the user into their clever linked account via GraphQL
 *
 * @param {CleverLoginVariables} variables The variables that will be sent to the mutation.
 * @param {string} variables.code The code that was received from clever, this
 * will be exchanged for an auth token so we can fetch the users information
 * @param {boolean} variables.reauth True if this is a reauth request, rather
 * than a login request
 * @param {boolean} variables.isMap True if the user is coming from the Clever
 * Map application, rather than our KAD/Clever Library application
 * @param {boolean} variables.isLP True if the user is coming from the Clever
 * LearningPaths application, rather than our KAD/Clever Library application
 * @param {string} variables.referrerUrl The URL at which the request code was provided
 *
 * @returns {Promise<CleverResult>} The mutation result.
 */
export const loginWithClever = async (
    gqlFetch: GqlFetchFn,
    variables: loginWithCleverMutationType["variables"],
): Promise<CleverResult> => {
    try {
        const result = await gqlFetch(CLEVER_LOGIN_MUTATION, {
            variables,
        });
        if (!result.loginWithClever) {
            throw new KAError("invalid clever login response", Errors.Internal);
        }
        return handleCleverMutationResults(result.loginWithClever);
    } catch (error) {
        return handleCleverMutationError(error as Error);
    }
};

/**
 * Transfers a users Clever authentication from the mobile app to the web app
 *
 * @param {CleverTransferAuthVariables} variables The variables that will be sent to the mutation.
 * @param {string} variables.transferToken The transfer token that will be
 * exchanged on the server for the users Clever access code, which will
 * then be used to fetch the users data from Clever.
 * @param {boolean} variables.reauth True if this is a reauth request, rather
 * than a login request
 *
 * @returns {Promise<CleverResult>} The mutation result.
 */
export const transferCleverAuth = async (
    gqlFetch: GqlFetchFn,
    variables: transferCleverAuthMutationType["variables"],
): Promise<CleverResult> => {
    try {
        const result = await gqlFetch(CLEVER_TRANSFER_AUTH_MUTATION, {
            variables,
        });
        if (!result.transferCleverAuth) {
            throw new KAError(
                "invalid clever transfer auth response",
                Errors.Internal,
            );
        }
        return handleCleverMutationResults(result.transferCleverAuth);
    } catch (error) {
        return handleCleverMutationError(error as Error);
    }
};
